import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BinaryBackground } from '../components/BinaryBackground';
import { Terminal, Send, Calendar } from 'lucide-react';
import { sendEmailAuditReport } from '../utils/email';

interface AuditData {
  industry: string;
  customIndustry: string;
  specialty: string;
  responseTime: string;
  manualHours: number;
  currentAiUsage: string;
}

interface AuditResults {
  roi: string;
  timeSavings: number;
  recommendations: string[];
}

const industryRecommendations: Record<string, string[]> = {
  'Real Estate': [
    'AI-powered property matching for faster client recommendations',
    'Automated scheduling for property viewings',
    'Smart document processing for contracts and leases'
  ],
  'E-commerce': [
    'AI chatbots for 24/7 customer support and product recommendations',
    'Automated inventory management and demand forecasting',
    'Smart email marketing campaigns with personalized content'
  ],
  'Automotive': [
    'AI-driven service scheduling and maintenance reminders',
    'Automated inventory tracking and parts ordering',
    'Smart lead qualification for sales inquiries'
  ],
  'Finance': [
    'AI-powered risk assessment and fraud detection',
    'Automated document processing and verification',
    'Smart customer onboarding and KYC automation'
  ],
  'Healthcare': [
    'AI appointment scheduling and patient reminders',
    'Automated medical record management',
    'Smart patient follow-up systems'
  ],
  'Building & Trade': [
    'AI-powered project scheduling and resource allocation',
    'Automated quote generation and material calculations',
    'Smart job tracking and progress reporting'
  ],
  'Hospitality & Restaurants': [
    'AI reservation management and table optimization',
    'Automated inventory and supply chain management',
    'Smart staff scheduling based on demand prediction'
  ],
  'Marketing & Advertising': [
    'AI-driven campaign optimization and analytics',
    'Automated social media management and content scheduling',
    'Smart audience targeting and segmentation'
  ],
  'Legal & Professional Services': [
    'AI-powered document review and analysis',
    'Automated client intake and case management',
    'Smart deadline tracking and compliance monitoring'
  ],
  'Education & Training': [
    'AI-driven student progress tracking and assessment',
    'Automated course content management and delivery',
    'Smart scheduling for classes and resources'
  ]
};

const getSpecialtyRecommendations = (industry: string, specialty: string): string[] => {
  const specialtyLower = specialty.toLowerCase();
  
  const specialtyRecommendations: Record<string, Record<string, string[]>> = {
    'Automotive': {
      'mobile mechanics': [
        'AI scheduling system optimized for mobile service routes',
        'Automated parts inventory management with location tracking',
        'Smart customer notification system for arrival times'
      ],
      'luxury car': [
        'AI-powered personalized client communication',
        'Predictive maintenance alerts for premium vehicles',
        'VIP customer experience automation'
      ]
    },
    'Healthcare': {
      'private clinic': [
        'Smart patient scheduling with minimal wait times',
        'Automated prescription renewal system',
        'AI-driven patient engagement and follow-up'
      ],
      'dental': [
        'AI treatment plan visualization',
        'Automated appointment reminders with preparation instructions',
        'Smart recall system for regular checkups'
      ]
    },
    'E-commerce': {
      'handmade': [
        'AI product photography enhancement',
        'Custom order tracking and progress updates',
        'Automated craft inventory management'
      ],
      'fashion': [
        'AI size recommendation system',
        'Style matching automation',
        'Personalized fashion advice chatbot'
      ]
    }
  };

  // Check if we have specific recommendations for this industry and specialty
  for (const [key, value] of Object.entries(specialtyRecommendations[industry] || {})) {
    if (specialtyLower.includes(key)) {
      return value;
    }
  }

  // Return empty array if no specific recommendations found
  return [];
};

const getGenericRecommendations = (): string[] => [
  'AI-powered customer service automation',
  'Smart workflow optimization and task automation',
  'Automated data entry and processing',
  'Intelligent document management system'
];

export const AuditPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [auditData, setAuditData] = useState<AuditData>({
    industry: '',
    customIndustry: '',
    specialty: '',
    responseTime: '',
    manualHours: 0,
    currentAiUsage: ''
  });
  const [results, setResults] = useState<AuditResults | null>(null);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');

  const calculateResults = (data: AuditData): AuditResults => {
    const timeSavings = Math.round(data.manualHours * 0.7 * 4); // 70% of weekly hours * 4 weeks
    const recommendations = [];

    // Add specialty-specific recommendations if available
    if (data.specialty && data.industry !== 'Other') {
      const specialtyRecs = getSpecialtyRecommendations(data.industry, data.specialty);
      recommendations.push(...specialtyRecs);
    }

    // Add industry-specific recommendations
    if (data.industry === 'Other') {
      recommendations.push(...getGenericRecommendations());
    } else if (recommendations.length === 0 && industryRecommendations[data.industry]) {
      recommendations.push(...industryRecommendations[data.industry]);
    }

    // Add general recommendations based on user input
    if (data.responseTime !== 'Immediate') {
      recommendations.push('Implement AI chatbots for instant lead response and qualification');
    }
    if (data.manualHours > 20) {
      recommendations.push('Deploy workflow automation to reduce manual tasks');
    }
    if (data.currentAiUsage === 'No') {
      recommendations.push('Begin AI integration with core business processes for immediate efficiency gains');
    }

    return {
      roi: '200%+',
      timeSavings,
      recommendations
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const calculatedResults = calculateResults(auditData);
    setResults(calculatedResults);
    setStep(2);
    setLoading(false);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setEmailError('');

    try {
      if (results) {
        const success = await sendEmailAuditReport(email, results);
        if (success) {
          setEmailSent(true);
        } else {
          setEmailError('Failed to send email. Please try again later.');
        }
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailError('An error occurred while sending the email. Please try again later.');
    }

    setLoading(false);
  };

  const handleIndustryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setAuditData(prev => ({
      ...prev,
      industry: value,
      customIndustry: value === 'Other' ? prev.customIndustry : ''
    }));
  };

  return (
    <>
      <BinaryBackground />
      <div className="min-h-screen py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-cyan-400 neon-text mb-4">
              AI Automation Audit
            </h1>
            <p className="text-xl text-gray-300">
              Discover your business's AI automation potential
            </p>
          </motion.div>

          {step === 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="service-card p-6">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        &gt; Industry
                      </label>
                      <select
                        required
                        value={auditData.industry}
                        onChange={handleIndustryChange}
                        className="w-full bg-black border-2 border-cyan-500/30 rounded p-3 text-white focus:border-cyan-400 focus:outline-none"
                      >
                        <option value="">Select your industry</option>
                        {Object.keys(industryRecommendations).map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {auditData.industry === 'Other' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        transition={{ duration: 0.3 }}
                      >
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          &gt; Please specify your industry
                        </label>
                        <input
                          type="text"
                          required
                          value={auditData.customIndustry}
                          onChange={(e) => setAuditData({...auditData, customIndustry: e.target.value})}
                          placeholder="Enter your industry"
                          className="w-full bg-black border-2 border-cyan-500/30 rounded p-3 text-white focus:border-cyan-400 focus:outline-none"
                        />
                      </motion.div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        &gt; What does your business specialize in?
                      </label>
                      <input
                        type="text"
                        value={auditData.specialty}
                        onChange={(e) => setAuditData({...auditData, specialty: e.target.value})}
                        placeholder="e.g., Mobile Mechanics, Luxury Car Sales, Custom Home Builders"
                        className="w-full bg-black border-2 border-cyan-500/30 rounded p-3 text-white focus:border-cyan-400 focus:outline-none"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Optional, but helps us provide more targeted recommendations
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        &gt; Average Lead Response Time
                      </label>
                      <select
                        required
                        value={auditData.responseTime}
                        onChange={(e) => setAuditData({...auditData, responseTime: e.target.value})}
                        className="w-full bg-black border-2 border-cyan-500/30 rounded p-3 text-white focus:border-cyan-400 focus:outline-none"
                      >
                        <option value="">Select response time</option>
                        <option value="Immediate">Immediate</option>
                        <option value="5-30 minutes">5-30 minutes</option>
                        <option value="30+ minutes">30+ minutes</option>
                        <option value="1+ hour">1+ hour</option>
                        <option value="24+ hours">24+ hours</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        &gt; Hours Spent on Manual Tasks Per Week
                      </label>
                      <input
                        type="number"
                        required
                        min="0"
                        value={auditData.manualHours}
                        onChange={(e) => setAuditData({...auditData, manualHours: parseInt(e.target.value)})}
                        className="w-full bg-black border-2 border-cyan-500/30 rounded p-3 text-white focus:border-cyan-400 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        &gt; Current AI Automation Usage
                      </label>
                      <select
                        required
                        value={auditData.currentAiUsage}
                        onChange={(e) => setAuditData({...auditData, currentAiUsage: e.target.value})}
                        className="w-full bg-black border-2 border-cyan-500/30 rounded p-3 text-white focus:border-cyan-400 focus:outline-none"
                      >
                        <option value="">Select current usage</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="Exploring">Exploring</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="cta-button w-full mt-6 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <span className="animate-pulse">&gt; Analyzing Data...</span>
                    ) : (
                      <><Terminal className="w-5 h-5" /> Get My AI Audit Report</>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          )}

          {step === 2 && results && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              <div className="service-card p-6">
                <h2 className="text-2xl font-bold text-cyan-400 mb-6">
                  Your AI Potential Analysis
                  {auditData.industry === 'Other' && auditData.customIndustry && (
                    <span className="block text-lg font-normal mt-2">
                      Industry: {auditData.customIndustry}
                    </span>
                  )}
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-cyan-300 mb-2">Projected ROI</h3>
                    <p className="text-3xl font-bold text-green-400">{results.roi}</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-cyan-300 mb-2">Monthly Time Savings</h3>
                    <p className="text-3xl font-bold text-green-400">{results.timeSavings} hours</p>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-cyan-300 mb-2">AI Recommendations</h3>
                    <ul className="space-y-2">
                      {results.recommendations.map((rec, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-300">
                          <span className="text-cyan-400">→</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {!emailSent ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="service-card p-6"
                >
                  <h3 className="text-xl font-semibold text-cyan-300 mb-4">
                    Get Your Detailed AI Strategy Report
                  </h3>
                  {emailError && (
                    <div className="text-red-400 mb-4 p-3 border border-red-400 rounded">
                      {emailError}
                    </div>
                  )}
                  <form onSubmit={handleEmailSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        &gt; Business Email
                      </label>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-black border-2 border-cyan-500/30 rounded p-3 text-white focus:border-cyan-400 focus:outline-none"
                        placeholder="your@email.com"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="cta-button w-full flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <span className="animate-pulse">&gt; Sending...</span>
                      ) : (
                        <><Send className="w-5 h-5" /> Send My AI Report</>
                      )}
                    </button>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="service-card p-6 text-center"
                >
                  <p className="text-green-400 mb-4">
                    ✓ Your detailed AI strategy report has been sent!
                  </p>
                  <a
                    href="https://calendly.com/hypermeshai/ai-consultation"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-button inline-flex items-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    Schedule Your Free AI Strategy Call
                  </a>
                </motion.div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};